<template>
  <Modal
    :is-open="isOpen"
    icon="merge"
    :title="$t('ui.actions.merge')"
    description="Merge all activity from this agreement into another."
    :buttons="['close', 'ok']"
    close-text="Cancel"
    ok-text="Merge"
    min-width="500"
    max-width="500"
    overflow-behaviour="visible"
    :is-confirming="isLoading"
    :disable-close="isLoading"
    @close-modal="$emit('close')"
    @ok-modal="$emit('save')"
  >
    <Alert
      severity="error"
      title="Merging agreements cannot be reversed! Triple check before merging."
      :show-dismiss-button="false"
      :show-close-button="false"
      class="mb-4"
    />
    <Label>Merge into</Label>
    <Select
      :model-value="selectedAgreementID ?? undefined"
      :search-term="searchTerm"
      :options="agreementsList"
      value-key="agreementId"
      label-key="name"
      searchable
      :handle-search="false"
      :has-error="hasError"
      @update:model-value="$emit('update:selected-agreement-id', $event)"
      @update:search-term="$emit('update:search-term', $event)"
    >
      <template #option="slotProps">
        <div class="w-full max-w-[400px]">
          <div class="font-semibold flex gap-4 items-center justify-between">
            <div>{{ slotProps.option.name }}</div>
            <div class="text-sm text-quaternary">
              <Icon type="hashtag" />
              {{ slotProps.option.agreementId }}
            </div>
          </div>
          <div class="flex w-full">
            <div class="text-sm text-quaternary w-full">
              <Icon type="car" />
              {{ slotProps.option.vehicleModel }} [{{ slotProps.option.vrm }}]
            </div>
            <div class="text-sm text-quaternary">
              <Icon type="clock" />
              {{ dayjs(slotProps.option.endDate).format('DD/MM/YYYY') }}
            </div>
          </div>
        </div>
      </template>
    </Select>
    <InputError :has-error="hasError">{{ errorMessage }}</InputError>

    <div
      v-if="selectedAgreementID != null"
      class="mt-4"
    >
      #{{ fromAgreementID }} -> #{{ selectedAgreementID }}
    </div>
  </Modal>
</template>

<script setup lang="ts">
// components
import Modal from '@/components/modal/Modal.vue'
import Alert from '@/components/alert/Alert.vue'
import Label from '@/components/forms/Label.vue'
import Select from '@/components/forms/Select.vue'
import InputError from '@/components/forms/InputError.vue'
import Icon from '@/components/icon/Icon.vue'
// utilities
import dayjs from 'dayjs'

interface Agreement {
  agreementId: number
  name: string
  vehicleModel: string
  vrm: string
  endDate: string
}

interface Props {
  isOpen: boolean
  isLoading: boolean
  fromAgreementID: number
  selectedAgreementID: number | null
  searchTerm: string
  agreementsList: Agreement[]
  hasError: boolean
  errorMessage: string | null
}

defineProps<Props>()

defineEmits<{
  close: []
  save: []
  'update:selected-agreement-id': [id: number | null]
  'update:search-term': [term: string]
}>()
</script>
